import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Linkedin, Youtube, Mail, Sparkles, MessageCircle, Clock, Infinity as InfinityIcon, ArrowRight, ExternalLink } from "lucide-react";

const AI_APP_URL = "https://app.coachvox.ai/share/brandijo";

const AiNav = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-brand-dark/90 backdrop-blur-md py-4">
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
      <Link to="/" className="font-serif text-xl md:text-2xl font-semibold text-brand-mist" data-testid="ai-nav-logo">
        Brandi Jo Newman
      </Link>
      <div className="flex items-center gap-6">
        <Link
          to="/"
          className="font-sans text-sm tracking-wide text-brand-mist/70 hover:text-brand-gold transition-colors hidden sm:block"
          data-testid="ai-nav-home"
        >
          Home
        </Link>
        <a
          href={AI_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-sans text-sm tracking-wide bg-brand-gold text-white px-5 py-2 rounded-full hover:bg-brand-tiffany transition-colors"
          data-testid="ai-nav-trial-btn"
        >
          Chat Free
        </a>
      </div>
    </div>
  </nav>
);

const AiHero = () => (
  <section className="pt-32 pb-16 bg-brand-dark">
    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <p className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.25em] uppercase text-brand-tiffany mb-5" data-testid="ai-hero-label">
          <Sparkles className="w-4 h-4" />
          Brandi Jo AI · Free
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-mist leading-tight mb-5" data-testid="ai-hero-heading">
          Get to know <span className="text-brand-gold italic">Brandi Jo</span>.
        </h1>
        <p className="font-sans text-base md:text-lg text-brand-mist/60 max-w-2xl mx-auto mb-8" data-testid="ai-hero-subline">
          Brandi Jo AI is free. Learn everything that Brandi Jo knows.
          Life, business, golf and everything in between.
        </p>
        <a
          href={AI_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-sans text-base bg-brand-gold text-white px-8 py-4 rounded-full hover:bg-brand-tiffany transition-colors shadow-lg"
          data-testid="ai-hero-trial-btn"
        >
          Start Chatting Free
          <ArrowRight className="w-4 h-4" />
        </a>
        <p className="font-sans text-sm text-brand-mist/50 mt-4" data-testid="ai-hero-pricing">
          Free to use. No credit card. Available 24/7.
        </p>
      </motion.div>
    </div>
  </section>
);

const benefits = [
  {
    icon: InfinityIcon,
    title: "Unlimited Conversations",
    desc: "No caps, no limits. Ask as many questions as you want, whenever inspiration or a challenge strikes.",
  },
  {
    icon: Clock,
    title: "Available 24/7",
    desc: "It's like having me in your pocket. Middle of the night idea? Big decision before a morning meeting? I'm there.",
  },
  {
    icon: MessageCircle,
    title: "Life, Business & Golf",
    desc: "Trained on my story, my frameworks, and how I actually think. Life, business, golf and everything in between, answered the way I'd answer.",
  },
];

const AiBenefits = () => (
  <section className="py-16 bg-brand-dark border-t border-brand-gold/10">
    <div className="max-w-5xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {benefits.map((b, i) => (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            className="rounded-2xl border border-brand-gold/20 p-8 text-center"
            data-testid={`ai-benefit-${i}`}
          >
            <b.icon className="w-8 h-8 text-brand-tiffany mx-auto mb-4" />
            <h3 className="font-serif text-lg md:text-xl text-brand-mist mb-3">{b.title}</h3>
            <p className="font-sans text-sm text-brand-mist/60 leading-relaxed">{b.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const AiPricing = () => (
  <section className="py-16 bg-brand-dark border-t border-brand-gold/10">
    <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="rounded-2xl border border-brand-tiffany/30 p-10"
        data-testid="ai-pricing-card"
      >
        <p className="font-sans text-xs tracking-[0.25em] uppercase text-brand-tiffany mb-4">The Price</p>
        <p className="font-serif text-4xl sm:text-5xl text-brand-mist mb-2" data-testid="ai-pricing-amount">
          Free
        </p>
        <p className="font-sans text-sm text-brand-mist/60 mb-6">
          Unlimited 24/7 conversations with Brandi Jo AI. On me, for now.
        </p>
        <a
          href={AI_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-sans text-base bg-brand-gold text-white px-8 py-4 rounded-full hover:bg-brand-tiffany transition-colors"
          data-testid="ai-pricing-trial-btn"
        >
          Start Chatting Free
        </a>
        <p className="font-sans text-xs text-brand-mist/40 mt-4">No credit card. No catch. Just ask.</p>
      </motion.div>
    </div>
  </section>
);

const faqs = [
  {
    q: "Is it really free?",
    a: "Yes. Tap any button on this page, open the app, and start chatting. No credit card, no trial clock. Free, for now, because I want you to get to know me.",
  },
  {
    q: "What can I ask Brandi Jo AI?",
    a: "Anything. Lifestyle entrepreneurship, mindset, golf, retirement strategies or whatever you're wrestling with. It's trained on my story and frameworks, so the answers sound like me, not a generic bot.",
  },
  {
    q: "How is this different from ChatGPT?",
    a: "ChatGPT gives you the internet's average answer. Brandi Jo AI gives you mine: eleven years of tournament golf, thirty years of building companies, and the inner work that came after. One perspective, fully committed.",
  },
  {
    q: "Will it stay free?",
    a: "It's free for now. If that ever changes, everyone chatting with it will hear from me first. Another reason to start today.",
  },
];

const AiFaq = () => (
  <section className="py-16 bg-brand-dark border-t border-brand-gold/10">
    <div className="max-w-3xl mx-auto px-6 md:px-12">
      <div className="text-center mb-10">
        <p className="font-sans text-xs tracking-[0.25em] uppercase text-brand-tiffany mb-3">Questions</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-brand-mist" data-testid="ai-faq-heading">
          Before You <span className="italic text-brand-gold">Start</span>
        </h2>
      </div>
      <div className="space-y-4">
        {faqs.map((f, i) => (
          <motion.details
            key={i}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="group rounded-xl border border-brand-gold/20 px-6 py-5"
            data-testid={`ai-faq-item-${i}`}
          >
            <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-serif text-base md:text-lg text-brand-mist">
              {f.q}
              <span className="text-brand-gold transition-transform duration-300 group-open:rotate-45 text-xl leading-none">+</span>
            </summary>
            <p className="font-sans text-sm text-brand-mist/60 leading-relaxed mt-3">{f.a}</p>
          </motion.details>
        ))}
      </div>
    </div>
  </section>
);

const AiFooter = () => (
  <footer className="py-12 bg-brand-dark border-t border-brand-gold/10">
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="text-center md:text-left">
        <p className="font-serif text-xl text-brand-mist">Brandi Jo Newman</p>
        <p className="font-sans text-xs tracking-[0.15em] uppercase text-brand-mist/40 mt-1">
          Brandi Jo AI
        </p>
      </div>
      <div className="flex items-center gap-5">
        <a href="https://instagram.com/brandijonewman" target="_blank" rel="noopener noreferrer" className="text-brand-mist/50 hover:text-brand-gold transition-colors" data-testid="ai-footer-instagram">
          <Instagram className="w-5 h-5" />
        </a>
        <a href="https://linkedin.com/in/brandijonewman" target="_blank" rel="noopener noreferrer" className="text-brand-mist/50 hover:text-brand-gold transition-colors" data-testid="ai-footer-linkedin">
          <Linkedin className="w-5 h-5" />
        </a>
        <a href="https://youtube.com/@brandijonewman365" target="_blank" rel="noopener noreferrer" className="text-brand-mist/50 hover:text-brand-gold transition-colors" data-testid="ai-footer-youtube">
          <Youtube className="w-5 h-5" />
        </a>
        <a href="mailto:me@brandijo.com" className="text-brand-mist/50 hover:text-brand-gold transition-colors" data-testid="ai-footer-email">
          <Mail className="w-5 h-5" />
        </a>
      </div>
      <p className="font-sans text-xs text-brand-mist/30 tracking-wide">
        © {new Date().getFullYear()} Brandi Jo Newman. All rights reserved. &nbsp;·&nbsp;{" "}
        <Link to="/terms" className="hover:text-brand-gold transition-colors underline" data-testid="ai-footer-terms-link">Terms</Link> &nbsp;·&nbsp;{" "}
        <Link to="/privacy" className="hover:text-brand-gold transition-colors underline" data-testid="ai-footer-privacy-link">Privacy</Link>
      </p>
    </div>
  </footer>
);

export default function AiPage() {
  return (
    <div className="App bg-brand-dark min-h-screen">
      <div className="noise-overlay" />
      <AiNav />
      <AiHero />
      <AiBenefits />
      <AiPricing />
      <AiFaq />
      <AiFooter />
    </div>
  );
}
